/**
 * P5.3 — Cluster Score Badge
 *
 * Single 0-100 KPI shown as a colored chip on the cluster header. Tooltip
 * exposes the per-component breakdown so operators can see WHY the score
 * is low.
 *
 * Color thresholds (deliberately wide so transient drops don't flicker):
 *   ≥ 70 → green  (healthy)
 *   40-69 → amber  (warning)
 *   < 40 → red    (needs attention)
 *
 * When backend reports `degraded: true` (one of the 5 inputs is >24h stale)
 * we show "Degraded" in gray instead of a number — better than a wrong number.
 *
 * Backend route: GET /api/v1/clusters/{cluster_id}/score
 *   ?recompute=true to force-refresh (operators see the spinner briefly).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Loader2, RefreshCw } from 'lucide-react';

const COMPONENT_LABELS = {
    utilization:       'Utilization',
    drift_penalty:     'Drift',
    realization:       'Realization',
    commitment_fit:    'Commitment fit',
    predicted_savings: 'Savings ceiling',
};

const formatPct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);
const formatAge = (sec) => {
    if (sec == null || sec >= 999999) return 'no data';
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
    return `${Math.round(sec / 86400)}d ago`;
};

const colorClass = (score, degraded) => {
    if (degraded) return 'bg-gray-200 text-gray-700 border-gray-300';
    if (score == null) return 'bg-gray-100 text-gray-500 border-gray-200';
    if (score >= 70) return 'bg-green-100 text-green-800 border-green-300';
    if (score >= 40) return 'bg-amber-100 text-amber-800 border-amber-300';
    return 'bg-red-100 text-red-800 border-red-300';
};

export default function ClusterScoreBadge({ clusterId }) {
    const [score, setScore] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const fetchScore = useCallback(async (recompute = false) => {
        if (!clusterId) return;
        try {
            if (recompute) setRefreshing(true);
            const response = await axios.get(
                `${API}/v1/clusters/${clusterId}/score`,
                { params: recompute ? { recompute: true } : {} },
            );
            setScore(response.data);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.detail || 'failed to load score');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [clusterId]);

    useEffect(() => {
        fetchScore(false);
        // Refresh every 5 min — auto_optimizer writes the cached value at
        // the end of each cycle (10 min interval typical), so polling at
        // 5 min catches an update within one cycle.
        const id = setInterval(() => fetchScore(false), 5 * 60_000);
        return () => clearInterval(id);
    }, [fetchScore]);

    if (loading) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border bg-gray-50">
                <Loader2 className="w-3 h-3 animate-spin" /> Score…
            </span>
        );
    }

    if (error) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border bg-red-50 text-red-700">
                Score error: {error}
            </span>
        );
    }

    const { score: s, degraded, breakdown, reason, from_cache } = score || {};

    // Tooltip: per-component values + freshness. Native title="" — keeps
    // this component free of additional UI library dependencies.
    const tooltipLines = [];
    if (reason) tooltipLines.push(reason);
    if (breakdown) {
        Object.entries(breakdown).forEach(([k, v]) => {
            const label = COMPONENT_LABELS[k] || k;
            tooltipLines.push(
                `${label}: ${formatPct(v.value)} (weight ${Math.round(v.weight * 100)}%, ${formatAge(v.freshness_seconds)})`
            );
        });
    }
    if (from_cache === false) tooltipLines.push('(just recomputed)');

    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded border ${colorClass(s, degraded)}`}
            title={tooltipLines.join('\n')}
        >
            Score: {degraded ? 'Degraded' : Math.round(s)}
            <button
                type="button"
                onClick={() => fetchScore(true)}
                title="Recompute now"
                className="ml-1 opacity-60 hover:opacity-100"
                disabled={refreshing}
            >
                {refreshing
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <RefreshCw className="w-3 h-3" />}
            </button>
        </span>
    );
}
